import {
  IBankPaymentCode,
  ICollectorAccountCodes,
} from '../../../../common/interfaces/bankPaymentCode.interface'
import { BankHabitatCode } from './BankHabitatCode'
import { CollectorAccountRow } from './CollectorAccountRow'
import { WarningHint } from './WarningHint'

interface IProps {
  bank: IBankPaymentCode
  isToggling: boolean
  isSavingBankCode: boolean
  savingAccountId?: number
  onToggle: (bank: IBankPaymentCode, enabled: boolean) => void
  onSaveBankHabitatCode: (bank: IBankPaymentCode, code: string) => Promise<boolean>
  onSaveAccountCodes: (
    accountId: number,
    fundTitle: string,
    codes: ICollectorAccountCodes
  ) => Promise<boolean>
}

const PAYMENT_CODE_STATUS = {
  enabled: {
    label: 'Habilitado',
    badgeClass: 'badge-light-success',
    actionLabel: 'Deshabilitar código de pago',
    actionClass: 'btn-light-danger',
  },
  disabled: {
    label: 'Deshabilitado',
    badgeClass: 'badge-light-danger',
    actionLabel: 'Habilitar código de pago',
    actionClass: 'btn-solid-primary',
  },
}

const MISSING_BANK_HABITAT_CODE_HINT =
  'Registre el código Habitat del banco para habilitar el código de pago.'

export const BankPaymentCodeCard = ({
  bank,
  isToggling,
  isSavingBankCode,
  savingAccountId,
  onToggle,
  onSaveBankHabitatCode,
  onSaveAccountCodes,
}: IProps) => {
  const enabled = bank.habitat_payment_code_enabled
  const status = enabled ? PAYMENT_CODE_STATUS.enabled : PAYMENT_CODE_STATUS.disabled
  const blockedByMissingBankCode = !enabled && !bank.habitat_bank_code?.trim()

  return (
    <div className='card p-10' data-testid={`bank-payment-code-${bank.id}`}>
      <div className='d-flex justify-content-between align-items-center gap-5'>
        <div className='d-flex align-items-center gap-3'>
          <h3 className='text-gray-800 fs-4 fw-bold m-0'>{bank.description}</h3>
          <span className={`badge ${status.badgeClass}`}>{status.label}</span>
        </div>
        <div className='d-flex align-items-center gap-3 flex-shrink-0'>
          {blockedByMissingBankCode && (
            <WarningHint
              label='Por qué no se puede habilitar el código de pago'
              message={MISSING_BANK_HABITAT_CODE_HINT}
            />
          )}
          <button
            type='button'
            className={`btn btn-sm ${status.actionClass} text-nowrap flex-shrink-0`}
            disabled={isToggling || blockedByMissingBankCode}
            onClick={() => onToggle(bank, !enabled)}
          >
            {isToggling ? (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Guardando...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            ) : (
              status.actionLabel
            )}
          </button>
        </div>
      </div>

      <div className='mt-5'>
        <BankHabitatCode bank={bank} saving={isSavingBankCode} onSave={onSaveBankHabitatCode} />
      </div>

      <div className='separator my-6' />

      <div className='table-responsive'>
        <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3 mb-0'>
          <thead>
            <tr className='fw-bolder text-muted'>
              <th className='min-w-250px'>FONDO</th>
              <th className='min-w-175px'>CÓDIGO SPECTRUM</th>
              <th className='min-w-150px'>CÓDIGO HABITAT</th>
              <th className='min-w-175px' />
            </tr>
          </thead>
          <tbody>
            {bank.collector_accounts.map((account) => (
              <CollectorAccountRow
                key={account.id}
                account={account}
                bankEnabled={enabled}
                saving={savingAccountId === account.id}
                onSave={onSaveAccountCodes}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
